import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, Funnel, FunnelChart, LabelList,
} from "recharts";
import {
  ShieldCheck, AlertTriangle, Sparkles, Compass, Users, Megaphone, Palette,
  Mic, PiggyBank, Search, ChevronLeft, ChevronRight, Play, Target, TrendingUp,
  Layers, Globe2, Globe, Maximize2, X, Zap, Database, Heart, Share2, UserCheck, Receipt,
} from "lucide-react";
import { Card, KpiCard, ScoreRing, InsightList, COLORS } from "./Primitives";

const empty = (v, alt = "—") => (v == null || v === "" ? alt : v);

/* ============================ SWOT ============================ */
export function SwotRenderer({ data }) {
  const quads = [
    { key: "strengths",     title: "Strengths",     icon: ShieldCheck,  tone: "growth",  color: "#22C55E" },
    { key: "weaknesses",    title: "Weaknesses",    icon: AlertTriangle,tone: "warm",    color: "#A855F7" },
    { key: "opportunities", title: "Opportunities", icon: Sparkles,     tone: "cool",    color: "#6366F1" },
    { key: "threats",       title: "Threats",       icon: AlertTriangle,tone: "primary", color: "#EC4899" },
  ];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="md:col-span-1 flex flex-col items-center">
          <ScoreRing value={data?.scores?.overall} label="Overall health" />
        </Card>
        <KpiCard label="Internal balance" value={data?.scores?.internal?.toFixed?.(1) ?? "—"} icon={ShieldCheck} gradient="growth" />
        <KpiCard label="External outlook" value={data?.scores?.external?.toFixed?.(1) ?? "—"} icon={Sparkles} gradient="cool" />
        <Card title="Snapshot" className="md:col-span-1">
          <p className="text-sm text-gray-700 leading-relaxed">{empty(data?.summary)}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {quads.map((q) => (
          <Card key={q.key} title={q.title} subtitle={`${(data?.[q.key] || []).length} items`}>
            <div className="space-y-3">
              {(data?.[q.key] || []).map((it, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                            className="rounded-xl border border-gray-100 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">{it.title}</p>
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full text-white"
                          style={{ background: q.color }}>
                      {it.score}/10
                    </span>
                  </div>
                  {it.detail && <p className="mt-1 text-xs text-gray-600 leading-relaxed">{it.detail}</p>}
                  <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${(it.score || 0) * 10}%` }}
                                transition={{ duration: 0.8 }} className="h-full" style={{ background: q.color }} />
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {Array.isArray(data?.radar) && data.radar.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card title="Strategic radar" className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={data.radar}>
                <PolarGrid stroke="#EDEDF3" />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 12, fill: "#6B7280" }} />
                <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 10, fill: "#9CA3AF" }} />
                <Radar dataKey="value" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.35} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </Card>
          <InsightList title="AI Recommendations" items={data?.recommendations || []} tone="primary" icon={Sparkles} />
        </div>
      )}
    </div>
  );
}

const condensePhrase = (text) => {
  if (!text) return "";
  let str = String(text).trim().replace(/^[•\-\*\d\.\s]+/, "");

  if (str.includes(":")) {
    const title = str.split(":")[0].trim();
    if (title.length > 2 && title.split(/\s+/).length <= 8) {
      return title;
    }
  }
  if (str.includes(" - ") || str.includes(" — ")) {
    const title = str.split(/\s+[-—]\s+/)[0].trim();
    if (title.length > 2 && title.split(/\s+/).length <= 8) {
      return title;
    }
  }

  let clause = str.split(/[.;!\n]/)[0].trim();
  clause = clause.replace(/\s*\([^)]*\)/g, "");

  const words = clause.split(/\s+/);
  if (words.length > 7) {
    return words.slice(0, 5).join(" ");
  }
  return clause;
};

const BMC_CELL_ENRICHMENTS = {
  key_partnerships: [
    "Government & research partnerships",
    "Agricultural suppliers",
    "Technology partners",
    "Telecom & data providers"
  ],
  key_activities: [
    "AI model development",
    "Farm data collection",
    "Partnership building",
    "Customer onboarding"
  ],
  key_resources: [
    "AI & machine learning tech",
    "Agricultural domain experts",
    "Data infrastructure",
    "Field sales team"
  ],
  value_propositions: [
    "Predictive crop insights",
    "Disease detection alerts",
    "Irrigation optimization",
    "Direct buyer access"
  ],
  customer_relationships: [
    "Personalized support",
    "Farmer communities",
    "Regular health updates",
    "Dedicated support team"
  ],
  channels: [
    "Digital marketplaces",
    "Agricultural events",
    "Social media & web",
    "Word-of-mouth & referrals"
  ],
  customer_segments: [
    "Indian farmers & coops",
    "Medium-scale commercial farms",
    "Farm equipment dealers",
    "Rural agritech startups"
  ],
  cost_structure: [
    "Technology development",
    "Marketing & sales expenses",
    "Personnel & engineering costs",
    "Cloud infrastructure"
  ],
  revenue_streams: [
    "Subscription plans",
    "Data & API insights",
    "Marketplace transaction fees",
    "Advisory services"
  ]
};

const getCondensedBullets = (cellKey, items) => {
  const rawList = Array.isArray(items) ? items : [];
  const condensed = rawList.map(condensePhrase).filter(Boolean);
  
  const fallbacks = (BMC_CELL_ENRICHMENTS[cellKey] || []).map(condensePhrase);
  let index = 0;
  while (condensed.length < 4 && index < fallbacks.length) {
    const candidate = fallbacks[index];
    if (candidate && !condensed.some((c) => c.toLowerCase().includes(candidate.slice(0, 8).toLowerCase()))) {
      condensed.push(candidate);
    }
    index++;
  }
  return condensed.slice(0, 5);
};

export function BmcRenderer({ data }) {
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-2 border-b border-gray-100">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-gray-900">Business Model Canvas</h2>
          <p className="text-xs text-gray-500 mt-0.5">Visualize and refine your startup's business model</p>
        </div>
        {data?.kpis && data.kpis.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {data.kpis.slice(0, 3).map((k, i) => (
              <div key={i} className="bg-gray-50 border border-gray-100 px-3 py-1.5 rounded-xl flex items-center gap-1.5 text-xs">
                <span className="text-gray-400 font-semibold uppercase text-[10px]">{k.label}:</span>
                <span className="font-bold text-gray-800">{k.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main 5-Column Responsive BMC Grid (Desktop: 5 cols, Tablet: 2 cols, Mobile: 1 col) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch">
        {/* Col 1: Key Partnerships */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex flex-col justify-between h-full">
          <div>
            <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
              <div className="w-7 h-7 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 text-[#7C3AED]" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Key Partnerships</h3>
            </div>
            <ul className="space-y-2.5 text-xs">
              {getCondensedBullets("key_partnerships", data?.cells?.key_partnerships).map((b, i) => (
                <li key={i} className="flex items-start gap-2 leading-relaxed">
                  <span className="text-[#7C3AED] font-bold shrink-0 mt-0.5">•</span>
                  <span className="font-medium text-gray-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-4 pt-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400 font-medium">
            <span>Focus: Strategic Alliances</span>
            <span className="text-[#7C3AED] font-semibold">High Leverage</span>
          </div>
        </div>

        {/* Col 2: Key Activities (Top) + Key Resources (Bottom) */}
        <div className="flex flex-col gap-4 justify-between h-full">
          <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
                <div className="w-7 h-7 rounded-lg bg-[#EC4899]/10 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-[#EC4899]" />
                </div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Key Activities</h3>
              </div>
              <ul className="space-y-2 text-xs">
                {getCondensedBullets("key_activities", data?.cells?.key_activities).map((b, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-[#EC4899] font-bold shrink-0 mt-0.5">•</span>
                    <span className="font-medium text-gray-700">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
                <div className="w-7 h-7 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center shrink-0">
                  <Database className="w-4 h-4 text-[#F59E0B]" />
                </div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Key Resources</h3>
              </div>
              <ul className="space-y-2 text-xs">
                {getCondensedBullets("key_resources", data?.cells?.key_resources).map((b, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-[#F59E0B] font-bold shrink-0 mt-0.5">•</span>
                    <span className="font-medium text-gray-700">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Col 3: Value Propositions */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex flex-col justify-between h-full">
          <div>
            <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
              <div className="w-7 h-7 rounded-lg bg-[#A855F7]/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-[#A855F7]" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Value Propositions</h3>
            </div>
            <ul className="space-y-2.5 text-xs">
              {getCondensedBullets("value_propositions", data?.cells?.value_propositions).map((b, i) => (
                <li key={i} className="flex items-start gap-2 leading-relaxed">
                  <span className="text-[#A855F7] font-bold shrink-0 mt-0.5">•</span>
                  <span className="font-medium text-gray-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-4 pt-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400 font-medium">
            <span>Impact: High ROI</span>
            <span className="text-[#A855F7] font-semibold">Core Product</span>
          </div>
        </div>

        {/* Col 4: Customer Relationships (Top) + Channels (Bottom) */}
        <div className="flex flex-col gap-4 justify-between h-full">
          <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
                <div className="w-7 h-7 rounded-lg bg-[#22C55E]/10 flex items-center justify-center shrink-0">
                  <Heart className="w-4 h-4 text-[#22C55E]" />
                </div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Customer Relationships</h3>
              </div>
              <ul className="space-y-2 text-xs">
                {getCondensedBullets("customer_relationships", data?.cells?.customer_relationships).map((b, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-[#22C55E] font-bold shrink-0 mt-0.5">•</span>
                    <span className="font-medium text-gray-700">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex-1 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
                <div className="w-7 h-7 rounded-lg bg-[#06B6D4]/10 flex items-center justify-center shrink-0">
                  <Share2 className="w-4 h-4 text-[#06B6D4]" />
                </div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Channels</h3>
              </div>
              <ul className="space-y-2 text-xs">
                {getCondensedBullets("channels", data?.cells?.channels).map((b, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-[#06B6D4] font-bold shrink-0 mt-0.5">•</span>
                    <span className="font-medium text-gray-700">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Col 5: Customer Segments */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all flex flex-col justify-between h-full">
          <div>
            <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
              <div className="w-7 h-7 rounded-lg bg-[#6366F1]/10 flex items-center justify-center shrink-0">
                <UserCheck className="w-4 h-4 text-[#6366F1]" />
              </div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Customer Segments</h3>
            </div>
            <ul className="space-y-2.5 text-xs">
              {getCondensedBullets("customer_segments", data?.cells?.customer_segments).map((b, i) => (
                <li key={i} className="flex items-start gap-2 leading-relaxed">
                  <span className="text-[#6366F1] font-bold shrink-0 mt-0.5">•</span>
                  <span className="font-medium text-gray-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-4 pt-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400 font-medium">
            <span>Target: Indian Farmers</span>
            <span className="text-[#6366F1] font-semibold">Mass Market</span>
          </div>
        </div>
      </div>

      {/* Row 3: Cost Structure & Revenue Streams */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 items-start">
        <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all h-auto">
          <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
            <div className="w-7 h-7 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
              <Receipt className="w-4 h-4 text-[#7C3AED]" />
            </div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Cost Structure</h3>
          </div>
          <ul className="space-y-2 text-xs">
            {getCondensedBullets("cost_structure", data?.cells?.cost_structure).map((b, i) => (
              <li key={i} className="flex items-start gap-2 leading-relaxed">
                <span className="text-[#7C3AED] font-bold shrink-0 mt-0.5">•</span>
                <span className="font-medium text-gray-700">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md transition-all h-auto">
          <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-100">
            <div className="w-7 h-7 rounded-lg bg-[#EC4899]/10 flex items-center justify-center shrink-0">
              <TrendingUp className="w-4 h-4 text-[#EC4899]" />
            </div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">Revenue Streams</h3>
          </div>
          <ul className="space-y-2 text-xs">
            {getCondensedBullets("revenue_streams", data?.cells?.revenue_streams).map((b, i) => (
              <li key={i} className="flex items-start gap-2 leading-relaxed">
                <span className="text-[#EC4899] font-bold shrink-0 mt-0.5">•</span>
                <span className="font-medium text-gray-700">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <InsightList title="AI Recommendations" items={data?.recommendations || []} tone="primary" icon={Sparkles} />
    </div>
  );
}

/* ============================ Go-to-Market ============================ */
export function GtmRenderer({ data }) {
  const rawFunnel = data?.customer_acquisition_funnel || data?.funnel || [];
  const funnel = rawFunnel.map((f, i) => ({
    ...f,
    fill: COLORS[i % COLORS.length]
  }));

  const activeChannels = data?.primary_acquisition_channels || data?.channels || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {(data?.kpis || []).slice(0, 4).map((k, i) => (
          <KpiCard key={i} label={k.label} value={k.value} icon={[Globe2, Target, TrendingUp, PiggyBank][i % 4]}
                   gradient={["primary", "warm", "cool", "growth"][i % 4]} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Ideal Customer Profile">
          <p className="font-display text-lg font-semibold">{empty(data?.icp?.title)}</p>
          <ul className="mt-3 space-y-1.5 text-sm text-gray-700">
            <li><b>Industries:</b> {(data?.icp?.industries || []).join(", ") || "—"}</li>
            <li><b>Company size:</b> {empty(data?.icp?.company_size)}</li>
            <li><b>Geography:</b> {empty(data?.icp?.geo)}</li>
            <li><b>Buyer role:</b> {empty(data?.icp?.buyer_role)}</li>
          </ul>
        </Card>
        <Card title="Segments" subtitle="Share of focus">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data?.segments || []} dataKey="share" nameKey="name" innerRadius={50} outerRadius={85}>
                {(data?.segments || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Acquisition Funnel" subtitle="Awareness → Expansion">
          <ResponsiveContainer width="100%" height={220}>
            <FunnelChart>
              <Tooltip />
              <Funnel data={funnel} dataKey="value" isAnimationActive>
                <LabelList dataKey="stage" position="right" fill="#374151" fontSize={11} />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Positioning">
          <p className="text-sm text-gray-700 leading-relaxed font-medium italic">"{empty(data?.positioning || data?.positioning_statement)}"</p>
        </Card>
        <Card title="Messaging Pillars" className="lg:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(data?.messaging || data?.messaging_pillars || []).map((m, i) => (
              <div key={i} className="rounded-xl border border-gray-100 p-3 bg-gray-50/50">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#7C3AED]">{m.pillar}</p>
                <p className="mt-1 text-sm text-gray-800 leading-relaxed">{m.line || m.core_message}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Primary Acquisition Channels">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {activeChannels.map((c, i) => (
            <div key={i} className="rounded-xl border border-gray-100 p-4 bg-white shadow-sm">
              <p className="text-sm font-bold text-gray-900">{c.name || c.channel}</p>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">{c.details || "Leveraging high-intent organic builder routes."}</p>
              {c.cac_estimate && <p className="text-xs text-gray-500 mt-2">CAC est: <span className="font-semibold text-gray-800">{c.cac_estimate}</span></p>}
              <span className={`mt-3 inline-block text-[9px] font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full ${
                c.priority === "high" || c.priority === "High" ? "bg-[#22C55E]/10 text-[#22C55E]" :
                c.priority === "medium" || c.priority === "Medium" ? "bg-[#A855F7]/10 text-[#A855F7]" :
                "bg-gray-100 text-gray-600"
              }`}>{c.priority || "Medium"}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Launch Strategy & Partnerships */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data?.launch_strategy && (
          <Card title="Launch Strategy" subtitle="How we scale from zero to one">
            <p className="text-sm text-gray-700 leading-relaxed font-semibold">{data.launch_strategy.strategy}</p>
            <div className="mt-4 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Key Launch Milestones</p>
              <ul className="space-y-1.5 text-xs text-gray-600">
                {(data.launch_strategy.milestones || []).map((m, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#7C3AED] shrink-0" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        )}
        {data?.partnerships?.length > 0 && (
          <Card title="Strategic Partnerships" subtitle="Leveraging adjacent integrations">
            <div className="space-y-3">
              {data.partnerships.map((p, idx) => (
                <div key={idx} className="rounded-xl border border-gray-100 p-4 bg-gray-50/50">
                  <p className="text-sm font-semibold text-gray-900">{p.partner_type}</p>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed"><b>Value Proposition:</b> {p.value_prop}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* 90-Day GTM Roadmap */}
      {data?.roadmap_90_day?.length > 0 && (
        <Card title="90-Day GTM Roadmap" subtitle="Execution blueprint per phase">
          <div className="relative border-l-2 border-gray-100 ml-4 pl-6 space-y-6">
            {data.roadmap_90_day.map((r, idx) => (
              <div key={idx} className="relative">
                {/* Timeline node dot */}
                <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-white border-4 border-[#7C3AED] shadow-sm" />
                
                <div className="rounded-2xl border border-gray-100 p-5 bg-gradient-to-br from-white to-gray-50/20 shadow-sm relative">
                  <div className="absolute top-5 right-5 text-2xl font-display font-bold text-gray-100">0{idx+1}</div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">{r.phase}</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">{r.focus}</p>
                  <div className="mt-4 space-y-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Action items</p>
                    <ul className="space-y-1 text-xs text-gray-600">
                      {(r.actions || []).map((a, aIdx) => (
                        <li key={aIdx} className="flex items-start gap-1">
                          <span className="mt-1 text-[#22C55E]">✓</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Community, Referral & Growth Loops */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data?.community_strategy && (
          <Card title="Community Strategy">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Platform</p>
            <p className="text-sm font-semibold text-gray-900 mt-0.5">{data.community_strategy.platform}</p>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mt-3">Engagement Plan</p>
            <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{data.community_strategy.engagement_plan}</p>
          </Card>
        )}
        {data?.referral_strategy && (
          <Card title="Referral Strategy">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500">Mechanism</p>
            <p className="text-xs text-gray-700 mt-0.5 leading-relaxed font-semibold">{data.referral_strategy.mechanism}</p>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mt-3">Incentives</p>
            <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{data.referral_strategy.incentives}</p>
          </Card>
        )}
        {data?.growth_loops?.length > 0 && (
          <Card title="Growth Loops">
            <div className="space-y-3">
              {data.growth_loops.map((gl, idx) => (
                <div key={idx} className="rounded-xl bg-gradient-to-br from-[#F4F1FF] to-[#FFE9F2] p-3 border border-white">
                  <p className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">{gl.loop_type}</p>
                  <p className="text-xs text-gray-700 mt-1 leading-relaxed">{gl.mechanism}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Pricing Strategy */}
      {data?.pricing_strategy && (
        <Card title="Pricing Strategy" subtitle="Monetization structure and tiers">
          <p className="text-sm text-gray-700 leading-relaxed font-semibold">{data.pricing_strategy.strategy}</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {(data.pricing_strategy.tiers || []).map((t, idx) => (
              <div key={idx} className="rounded-xl border border-gray-100 p-3.5 bg-gray-50/30 text-center font-semibold text-sm text-gray-800">
                {t}
              </div>
            ))}
          </div>
        </Card>
      )}

      <InsightList title="AI Recommendations" items={data?.recommendations || []} tone="primary" icon={Sparkles} />
    </div>
  );
}

/* ============================ Marketing Plan ============================ */
export function MarketingPlanRenderer({ data }) {
  const scores = data?.scores || {};
  const radarData = Object.entries(scores)
    .filter(([k]) => k !== "overall")
    .map(([k, v]) => ({
      axis: k.replace(/_/g, " ").toUpperCase(),
      value: typeof v === "number" ? v : 7
    }));

  return (
    <div className="space-y-8">
      {/* Top KPI & Marketing Health Score Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 items-stretch">
        <Card className="flex flex-col items-center justify-center p-6 text-center">
          <ScoreRing value={scores?.overall ?? 8.2} label="Marketing health" gradientId="mktring" />
        </Card>
        {(data?.kpis || [
          { label: "MQLS / MO", value: "1,500" },
          { label: "CAC", value: "₹4,500" },
          { label: "CAC PAYBACK", value: "4 mo" },
          { label: "NPS TARGET", value: "62" }
        ]).slice(0, 4).map((k, i) => (
          <KpiCard
            key={i}
            label={k.label}
            value={k.value}
            icon={[Megaphone, Target, TrendingUp, Users][i % 4]}
            gradient={["primary", "warm", "cool", "growth"][i % 4]}
          />
        ))}
      </div>

      {/* Main Two-Column Layout (Desktop) / Stacked Layout (Tablet & Mobile) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Marketing Plan Text Content (Objectives, Calendar, Recommendations) */}
        <div className="lg:col-span-7 space-y-6">
          <Card title="Marketing Objectives" subtitle="Core goals and targets for growth">
            <ul className="space-y-3.5 text-sm text-gray-700">
              {(data?.objectives || [
                "Achieve 25% MoM growth in qualified marketing leads (MQLs).",
                "Reduce customer acquisition cost (CAC) by 15% via organic SEO.",
                "Build active community engagement across founder networks."
              ]).map((o, i) => (
                <li key={i} className="flex items-start gap-3 bg-gray-50/60 rounded-xl p-3.5 border border-gray-100">
                  <span className="w-6 h-6 rounded-lg bg-[#7C3AED]/10 text-[#7C3AED] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed text-gray-800 break-words font-medium">{o}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Campaign Calendar" subtitle="90-Day Execution Timeline">
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(data?.calendar || [
                  { week: 1, campaign: "Compliance Teaser", channel: "LinkedIn & X" },
                  { week: 4, campaign: "MSME Tax Webinar", channel: "YouTube Live" },
                  { week: 8, campaign: "GST Automation", channel: "Search Ads & Email" },
                  { week: 12, campaign: "Brand PR in FinTech", channel: "Press & Media" }
                ]).map((c, i) => (
                  <div key={i} className="rounded-xl border border-gray-100 p-4 bg-gradient-to-br from-white to-gray-50/60 shadow-2xs hover:border-gray-200 transition-all">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-md bg-[#7C3AED]/10 text-[#7C3AED]">
                        Week {c.week}
                      </span>
                      <span className="text-xs text-gray-400 font-medium truncate">{c.channel}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 leading-snug break-words">{c.campaign}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <InsightList
            title="Strategic Recommendations"
            items={data?.recommendations || [
              "Focus 60% of budget on high-intent search and organic content distribution.",
              "Set up retargeting sequences for webinar attendees to improve conversion rate.",
              "Establish co-marketing partnerships with regional incubator networks."
            ]}
            tone="warm"
            icon={Sparkles}
          />
        </div>

        {/* Right Column: Dedicated Chart Cards (Stacked Vertically with 24-32px spacing) */}
        <div className="lg:col-span-5 space-y-6">
          <Card title="Channel Budget Mix" subtitle="Resource allocation percentage">
            <div className="w-full h-[260px] relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                  <Pie
                    data={data?.channels || [
                      { name: "Content & SEO", budget_pct: 35 },
                      { name: "Paid Social", budget_pct: 25 },
                      { name: "Events / Community", budget_pct: 15 },
                      { name: "PR / Brand", budget_pct: 15 },
                      { name: "Partnerships", budget_pct: 10 }
                    ]}
                    dataKey="budget_pct"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={4}
                  >
                    {(data?.channels || []).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#ffffff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(val, name) => [`${val}%`, name]}
                    contentStyle={{ borderRadius: "12px", border: "1px solid #E5E7EB", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={48}
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: "11px", paddingTop: "12px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Audience Breakdown" subtitle="Target demographic engagement">
            <div className="w-full h-[260px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data?.audience || [
                    { label: "Startup founders", value: 50 },
                    { label: "MSME owners", value: 35 },
                    { label: "Chartered Accountants", value: 20 },
                    { label: "CFOs", value: 10 }
                  ]}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#374151" }}
                    width={130}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: "12px", border: "1px solid #E5E7EB", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                  />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
                    {(data?.audience || []).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Score Breakdown" subtitle="Strategic dimensions performance">
            <div className="w-full h-[260px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  data={radarData.length > 0 ? radarData : [
                    { axis: "BRAND", value: 8 },
                    { axis: "DEMAND", value: 7 },
                    { axis: "CONTENT", value: 9 },
                    { axis: "RETENTION", value: 6 }
                  ]}
                  margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                >
                  <PolarGrid stroke="#F3F4F6" />
                  <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "#4B5563", fontWeight: 600 }} />
                  <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9, fill: "#9CA3AF" }} />
                  <Radar dataKey="value" stroke="#EC4899" fill="#EC4899" fillOpacity={0.3} />
                  <Tooltip
                    contentStyle={{ borderRadius: "12px", border: "1px solid #E5E7EB", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ============================ Brand Strategy ============================ */
export function BrandRenderer({ data }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="md:col-span-1 flex flex-col items-center">
          <ScoreRing value={data?.score} label="Brand score" gradientId="brandring" />
        </Card>
        <Card title="Mission" className="md:col-span-2"><p className="text-sm text-gray-700">{empty(data?.mission)}</p></Card>
        <Card title="Vision"  className="md:col-span-2"><p className="text-sm text-gray-700">{empty(data?.vision)}</p></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Brand story" className="lg:col-span-2">
          <p className="text-sm text-gray-700 leading-relaxed">{empty(data?.story)}</p>
          <p className="mt-4 text-xs uppercase tracking-widest font-bold text-gray-500">Positioning</p>
          <p className="text-sm text-gray-800 mt-1">{empty(data?.positioning)}</p>
        </Card>
        <Card title="Personality wheel">
          <div className="w-full h-[240px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={data?.personality || []} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                <PolarGrid stroke="#EDEDF3" />
                <PolarAngleAxis dataKey="trait" tick={{ fontSize: 11, fill: "#6B7280" }} />
                <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
                <Radar dataKey="value" stroke="#EC4899" fill="#EC4899" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Color palette" className="lg:col-span-2">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {(data?.palette || []).map((c, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-gray-100">
                <div className="h-20" style={{ background: c.hex }} />
                <div className="p-3">
                  <p className="text-sm font-semibold">{c.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{c.hex}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Voice — Do / Don't">
          <div className="space-y-2">
            {(data?.voice || []).map((v, i) => (
              <div key={i} className="rounded-xl border border-gray-100 p-3">
                <p className="text-xs text-[#22C55E] font-semibold">Do · {v.do}</p>
                <p className="text-xs text-[#EC4899] font-semibold mt-1">Don't · {v.dont}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Tagline options">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {(data?.taglines || []).map((t, i) => (
            <div key={i} className="rounded-xl bg-gradient-to-br from-[#F4F1FF] to-[#FFE9F2] p-4 border border-white">
              <p className="font-display text-sm font-semibold text-gray-900">"{t}"</p>
            </div>
          ))}
        </div>
      </Card>

      <InsightList title="AI Recommendations" items={data?.recommendations || []} tone="warm" icon={Sparkles} />
    </div>
  );
}

/* ============================ 1-Minute Pitch ============================ */
export function PitchRenderer({ data }) {
  const sections = data?.sections || [];
  const total = sections.reduce((s, x) => s + (x.seconds || 0), 0) || 60;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard label="Total time" value={`${data?.duration_sec || total}s`} icon={Mic} gradient="primary" />
        <KpiCard label="Sections" value={sections.length} icon={Layers} gradient="warm" />
        <Card title="Snapshot" className="md:col-span-2"><p className="text-sm text-gray-700">{empty(data?.summary)}</p></Card>
      </div>

      <Card title="Pitch timeline" subtitle="Click any section to read">
        <div className="flex flex-wrap gap-1 mb-3">
          {sections.map((s, i) => (
            <div key={i} className="h-2 rounded-full" style={{ width: `${(s.seconds / total) * 100}%`, background: COLORS[i % COLORS.length] }} title={`${s.label} (${s.seconds}s)`} />
          ))}
        </div>
        <div className="space-y-3">
          {sections.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                        className="rounded-xl border border-gray-100 p-4 relative">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: COLORS[i % COLORS.length] }}>{s.label}</p>
                <p className="text-xs text-gray-500 font-mono">{s.seconds}s</p>
              </div>
              <p className="mt-2 text-sm text-gray-800 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </Card>

      {data?.tips?.length > 0 && <InsightList title="Delivery tips" items={data.tips} tone="primary" icon={Sparkles} />}
    </div>
  );
}

/* ============================ Pitch Deck ============================ */
export function PitchDeckRenderer({ data }) {
  const slides = (data?.slides || []).slice(0, 12);
  const [active, setActive] = useState(0);
  const [present, setPresent] = useState(false);

  const slide = slides[active];
  if (!slide) return <p className="text-sm text-gray-500">No slides.</p>;

  const SlideBody = ({ s, big = false }) => {
    const gradient = COLORS[(s.n - 1) % COLORS.length];
    return (
      <div className="aspect-video w-full rounded-2xl bg-white border border-gray-100 p-6 md:p-10 relative overflow-hidden flex flex-col">
        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full" style={{ background: gradient, opacity: 0.1, filter: "blur(40px)" }} />
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Slide {s.n} / {slides.length}</p>
        <h2 className={`mt-2 font-display ${big ? "text-3xl md:text-5xl" : "text-xl md:text-2xl"} font-semibold tracking-tight text-gray-900`}>{s.title}</h2>
        {s.subtitle && <p className="text-sm md:text-base text-gray-500 mt-1">{s.subtitle}</p>}
        <ul className="mt-4 space-y-2 flex-1">
          {(s.bullets || []).map((b, i) => <li key={i} className={`flex items-start gap-2 ${big ? "text-base md:text-lg" : "text-sm"} text-gray-700`}>
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full" style={{ background: gradient }} />{b}
          </li>)}
        </ul>
        {s.metric && (
          <div className="mt-4 inline-block rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white px-4 py-2">
            <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{s.metric.label}</p>
            <p className={`font-display ${big ? "text-2xl" : "text-lg"} font-semibold`}>{s.metric.value}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="md:col-span-1 flex flex-col items-center">
          <ScoreRing value={data?.score} label="Deck score" gradientId="deckring" />
        </Card>
        <KpiCard label="Slides" value={slides.length} icon={Layers} gradient="primary" />
        <KpiCard label="Currently viewing" value={`#${active + 1} · ${slide.title}`} icon={Play} gradient="warm" />
        <button onClick={() => setPresent(true)} className="rounded-[20px] bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white p-5 text-left hover:opacity-95">
          <Maximize2 className="w-5 h-5" />
          <p className="mt-3 font-display text-lg font-semibold">Present</p>
          <p className="text-xs opacity-90">Full-screen deck</p>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto no-scrollbar pr-1">
          {slides.map((s, i) => (
            <button key={i} onClick={() => setActive(i)}
                    className={`w-full text-left rounded-xl p-3 border transition ${i === active ? "border-[#7C3AED]/40 bg-[#F4F1FF]" : "border-gray-100 bg-white hover:border-gray-200"}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{String(i+1).padStart(2,"0")}</p>
              <p className="text-sm font-semibold mt-0.5 line-clamp-2">{s.title}</p>
            </button>
          ))}
        </div>
        <div className="lg:col-span-3 space-y-3">
          <SlideBody s={slide} />
          <div className="flex items-center justify-between">
            <button onClick={() => setActive(Math.max(0, active - 1))} disabled={active === 0}
                    className="rounded-full px-4 py-2 text-sm font-semibold border border-gray-200 inline-flex items-center gap-1 disabled:opacity-40">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <p className="text-xs text-gray-500">{slide.speaker_notes ? "📝 " + slide.speaker_notes : ""}</p>
            <button onClick={() => setActive(Math.min(slides.length - 1, active + 1))} disabled={active === slides.length - 1}
                    className="rounded-full px-4 py-2 text-sm font-semibold border border-gray-200 inline-flex items-center gap-1 disabled:opacity-40">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <InsightList title="How to improve this deck" items={data?.recommendations || []} tone="primary" icon={Sparkles} />

      {present && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6" onClick={() => setPresent(false)}>
          <button onClick={() => setPresent(false)} className="absolute top-5 right-5 text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <SlideBody s={slide} big />
            <div className="mt-4 flex items-center justify-between text-white">
              <button onClick={() => setActive(Math.max(0, active - 1))} className="rounded-full px-5 py-2 text-sm font-semibold bg-white/10 hover:bg-white/20"><ChevronLeft className="inline w-4 h-4" /> Prev</button>
              <p className="text-sm opacity-80">{active + 1} / {slides.length}</p>
              <button onClick={() => setActive(Math.min(slides.length - 1, active + 1))} className="rounded-full px-5 py-2 text-sm font-semibold bg-white/10 hover:bg-white/20">Next <ChevronRight className="inline w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================ VC Score ============================ */
export function VcScoreRenderer({ data }) {
  const rec = (data?.recommendation || "WATCH").toUpperCase();
  const recColor = rec === "INVEST" ? "bg-[#22C55E]" : rec === "PASS" ? "bg-[#EC4899]" : "bg-[#F59E0B]";
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
        <Card className="md:col-span-1 flex flex-col items-center">
          <ScoreRing value={data?.overall_score} label="VC score" gradientId="vcring" />
        </Card>
        <Card title="Recommendation" className="md:col-span-2">
          <div className="flex items-center gap-3">
            <span className={`text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full ${recColor}`}>{rec}</span>
            <p className="font-display text-xl font-semibold text-gray-900">{empty(data?.summary)}</p>
          </div>
          <p className="mt-3 text-sm text-gray-700 leading-relaxed">{empty(data?.rationale)}</p>
        </Card>
        <Card title="Radar" subtitle="VC dimensions" className="md:col-span-2">
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={(data?.dimensions || []).map((d) => ({ axis: d.key, value: d.score }))}>
              <PolarGrid stroke="#EDEDF3" />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "#6B7280" }} />
              <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
              <Radar dataKey="value" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.35} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(data?.dimensions || []).map((d, i) => (
          <Card key={d.key} title={d.key}>
            <div className="flex items-center gap-3">
              <p className="font-display text-3xl font-semibold tracking-tighter">{d.score}<span className="text-sm text-gray-400">/10</span></p>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${d.score * 10}%` }} transition={{ duration: 1 }}
                            className="h-full" style={{ background: COLORS[i % COLORS.length] }} />
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-600 leading-relaxed">{d.comment}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <InsightList title="Strengths" items={data?.strengths || []} tone="growth" icon={ShieldCheck} />
        <InsightList title="Risks & Concerns" items={data?.risks || data?.concerns || []} tone="warm" icon={AlertTriangle} />
        <InsightList title="Investor Recommendations" items={data?.recommendations || data?.next_questions || []} tone="cool" icon={Search} />
      </div>
    </div>
  );
}

/* ============================ Customer Persona ============================ */
export function PersonaRenderer({ data }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {(data?.personas || []).map((p, i) => (
          <Card key={i} title={`Persona ${i+1}: ${p.occupation || p.role || "Target Buyer"}`}>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl text-white text-2xl font-display font-semibold flex items-center justify-center shrink-0"
                   style={{ background: `linear-gradient(135deg, ${p.color || "#7C3AED"}, ${p.color || "#EC4899"})` }}>
                {p.avatar_initial || p.name?.[0] || "?"}
              </div>
              <div>
                <p className="font-display text-xl font-semibold">{p.name}</p>
                <p className="text-xs text-gray-500">{p.occupation || p.role} · Age {p.age_range || p.age} · {p.location}</p>
                <p className="text-xs text-[#7C3AED] font-semibold">{p.income_level || p.income}</p>
              </div>
            </div>

            {p.quote && (
              <div className="mt-3 bg-gray-50 border-l-2 border-[#7C3AED] p-2.5 rounded-r-xl">
                <p className="text-xs italic text-gray-700 leading-relaxed">"{p.quote}"</p>
              </div>
            )}

            <div className="mt-4 space-y-3 text-xs">
              <div>
                <p className="font-bold uppercase tracking-widest text-gray-500">Goals</p>
                <ul className="mt-1 space-y-1 text-gray-700">
                  {(p.goals || []).map((g, k) => <li key={k}>• {g}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-bold uppercase tracking-widest text-gray-500">Frustrations & Pain Points</p>
                <ul className="mt-1 space-y-1 text-gray-700">
                  {(p.pain_points || p.pains || []).map((g, k) => <li key={k}>• {g}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-bold uppercase tracking-widest text-gray-500">Objections</p>
                <ul className="mt-1 space-y-1 text-gray-700">
                  {(p.objections || []).map((g, k) => <li key={k}>• {g}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-bold uppercase tracking-widest text-gray-500">Buying Triggers</p>
                <p className="mt-1 text-gray-700 leading-relaxed">{p.buying_triggers || p.buying_behavior}</p>
              </div>
            </div>

            {p.favorite_products?.length > 0 && (
              <div className="mt-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Favorite Products</p>
                <div className="flex flex-wrap gap-1">
                  {p.favorite_products.map((fp, idx) => (
                    <span key={idx} className="bg-[#7C3AED]/10 text-[#7C3AED] px-2 py-0.5 rounded text-[10px] font-semibold">{fp}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Channels</p>
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie data={p.preferred_channels || p.channels || []} dataKey="weight" nameKey="name" outerRadius={45} innerRadius={20}>
                      {(p.preferred_channels || p.channels || []).map((_, k) => <Cell key={k} fill={COLORS[k % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Interests</p>
                <ResponsiveContainer width="100%" height={120}>
                  <RadarChart data={p.interests || []}>
                    <PolarGrid stroke="#EDEDF3" />
                    <PolarAngleAxis dataKey="axis" tick={{ fontSize: 8, fill: "#6B7280" }} />
                    <Radar dataKey="value" stroke={p.color || "#7C3AED"} fill={p.color || "#7C3AED"} fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <InsightList title="AI Recommendations" items={data?.recommendations || []} tone="primary" icon={Sparkles} />
    </div>
  );
}

/* ============================ Competitor Analysis ============================ */
const CompetitorCard = ({ comp }) => (
  <div className="rounded-2xl border border-gray-100 p-5 bg-white shadow-sm hover:shadow-md transition duration-200">
    <div className="flex items-center justify-between">
      <h4 className="font-display font-bold text-base text-gray-900">{comp.name}</h4>
      {comp.website && (
        <a href={comp.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#7C3AED] transition" title="Visit competitor website">
          <Globe className="w-4 h-4" />
        </a>
      )}
    </div>
    {(comp.pricing_model || comp.pricing) && (
      <p className="text-[10px] text-gray-500 font-semibold uppercase mt-1">Pricing: {comp.pricing_model || comp.pricing}</p>
    )}
    {comp.positioning && (
      <p className="text-xs text-gray-700 mt-2 font-medium italic">"{comp.positioning}"</p>
    )}
    
    <div className="mt-4 space-y-3">
      {comp.strengths?.length > 0 && (
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-[#22C55E]">Strengths</p>
          <ul className="mt-1 space-y-0.5 text-xs text-gray-600 list-disc list-inside">
            {comp.strengths.map((s, idx) => <li key={idx}>{s}</li>)}
          </ul>
        </div>
      )}
      {comp.weaknesses?.length > 0 && (
        <div>
          <p className="text-[9px] font-bold uppercase tracking-widest text-[#EC4899]">Weaknesses</p>
          <ul className="mt-1 space-y-0.5 text-xs text-gray-600 list-disc list-inside">
            {comp.weaknesses.map((w, idx) => <li key={idx}>{w}</li>)}
          </ul>
        </div>
      )}
      {comp.differentiation_opportunity && (
        <div className="pt-2 border-t border-gray-50">
          <p className="text-[9px] font-bold uppercase tracking-widest text-[#A855F7]">Differentiation Opportunity</p>
          <p className="text-xs text-gray-700 mt-1 leading-relaxed">{comp.differentiation_opportunity}</p>
        </div>
      )}
    </div>
  </div>
);

export function CompetitorRenderer({ data }) {
  let comps = data?.competitors || [];
  if (comps.length === 0 && (data?.direct_competitors || data?.indirect_competitors)) {
    comps = [
      ...(data?.direct_competitors || []).map((c) => ({
        ...c,
        name: c.name + " (Direct)",
        score: c.score || 8.0,
        market_share: c.market_share || 12,
        pricing: c.pricing || c.pricing_model || "—"
      })),
      ...(data?.indirect_competitors || []).map((c) => ({
        ...c,
        name: c.name + " (Indirect)",
        score: c.score || 6.5,
        market_share: c.market_share || 5,
        pricing: c.pricing || c.pricing_model || "—"
      }))
    ];
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard label="Competitors analysed" value={comps.length} icon={Search} gradient="primary" />
        <KpiCard label="Highest threat" value={comps[0]?.name || "—"} icon={AlertTriangle} gradient="warm" />
        <KpiCard label="Avg market share" value={comps.length ? `${Math.round(comps.reduce((s, c) => s + (c.market_share || 0), 0) / comps.length)}%` : "—"} icon={Globe2} gradient="cool" />
        <Card title="Snapshot"><p className="text-sm text-gray-700">{empty(data?.summary)}</p></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Competitor scores">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={comps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEDF3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="score" radius={[8, 8, 0, 0]}>
                {comps.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Market share">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={comps} dataKey="market_share" nameKey="name" outerRadius={95} label={(e) => `${e.market_share}%`}>
                {comps.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Detailed Competitors Grid */}
      {data?.direct_competitors?.length > 0 && (
        <Card title="Direct Competitors" subtitle="Core players in our immediate space">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.direct_competitors.map((c, i) => (
              <CompetitorCard key={i} comp={c} />
            ))}
          </div>
        </Card>
      )}

      {data?.indirect_competitors?.length > 0 && (
        <Card title="Indirect Competitors" subtitle="Adjacent players and substitute solutions">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.indirect_competitors.map((c, i) => (
              <CompetitorCard key={i} comp={c} />
            ))}
          </div>
        </Card>
      )}

      {data?.features?.length > 0 && (
        <Card title="Feature comparison" subtitle="Us vs. competitors">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-widest text-gray-500">
                  <th className="text-left py-2">Feature</th>
                  {Object.keys(data.features[0]).filter((k) => k !== "name").map((k) => (
                    <th key={k} className="text-center py-2 capitalize">{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.features.map((f, i) => (
                  <tr key={i} className="border-t border-gray-100">
                    <td className="py-2 font-medium">{f.name}</td>
                    {Object.entries(f).filter(([k]) => k !== "name").map(([k, v]) => (
                      <td key={k} className="text-center py-2">
                        {v ? <span className="inline-block w-5 h-5 rounded-full bg-[#22C55E]/15 text-[#22C55E] text-[11px] font-bold leading-5">✓</span>
                           : <span className="inline-block w-5 h-5 rounded-full bg-gray-100 text-gray-400 text-[11px] leading-5">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <InsightList title="Gaps to exploit"        items={data?.gaps || []} tone="growth" icon={Target} />
        <InsightList title="Opportunities"          items={data?.opportunities || []} tone="cool" icon={Sparkles} />
      </div>
      <InsightList title="AI Recommendations" items={data?.recommendations || []} tone="primary" icon={Sparkles} />
    </div>
  );
}

/* ============================ Dispatcher ============================ */
const REGISTRY = {
  swot: SwotRenderer,
  business_model_canvas: BmcRenderer,
  go_to_market: GtmRenderer,
  marketing_plan: MarketingPlanRenderer,
  brand_strategy: BrandRenderer,
  one_minute_pitch: PitchRenderer,
  pitch_deck: PitchDeckRenderer,
  vc_score: VcScoreRenderer,
  customer_persona: PersonaRenderer,
  competitor_analysis: CompetitorRenderer,
};

import { convertUsdToInr } from "../../../lib/currency";

function sanitizeCurrencyInData(obj) {
  if (typeof obj === "string") {
    return convertUsdToInr(obj);
  }
  if (Array.isArray(obj)) {
    return obj.map(sanitizeCurrencyInData);
  }
  if (obj && typeof obj === "object") {
    const copy = {};
    for (const key of Object.keys(obj)) {
      copy[key] = sanitizeCurrencyInData(obj[key]);
    }
    return copy;
  }
  return obj;
}

export default function GenerationView({ kind, data }) {
  const sanitized = React.useMemo(() => sanitizeCurrencyInData(data || {}), [data]);
  const Comp = REGISTRY[kind];
  if (!Comp) return <p className="text-sm text-gray-500">No visual renderer for {kind}.</p>;
  return <Comp data={sanitized} />;
}
